import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

export const AuthContext = createContext(null);

// Pure, testable flag check. Fail-open: only an explicit `false` disables.
export function featureEnabled(features, key) {
  return !features || features[key] !== false;
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [features, setFeatures] = useState({});
    const [registry, setRegistry] = useState({});
    // False until the first flag fetch settles. An empty map reads as "all on"
    // (fail-open), so a route that swaps whole pages on a flag waits on this
    // rather than briefly mounting the wrong page.
    const [featuresLoaded, setFeaturesLoaded] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [adminAreas, setAdminAreas] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/me`, { withCredentials: true });
                setUser(response.data);
                setPermissions(response.data.permissions || []);
                setAdminAreas(response.data.admin_areas || []);
            } catch (error) {
                setUser(null);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    // Re-fetch flags from the server. Exposed so an admin toggling a feature
    // (AdminSettings) can refresh the live session without a page reload.
    // Fail-open: on error leave the current map untouched.
    const refreshFeatures = useCallback(async () => {
        try {
            const r = await axios.get(`${API_BASE_URL}/api/feature-flags`, { withCredentials: true });
            setFeatures(r.data.flags || {});
            setRegistry(r.data.registry || {});
        } catch {
            /* fail-open: leave features as-is ⇒ all enabled by default */
        } finally {
            setFeaturesLoaded(true);
        }
    }, []);

    // Flags load independently of auth resolution.
    useEffect(() => { refreshFeatures(); }, [refreshFeatures]);

    return (
        <AuthContext.Provider value={{ user, loading, features, featuresLoaded, registry, refreshFeatures, permissions, adminAreas }}>
            {loading ? (
                <div className="app-loading" role="status" aria-live="polite">
                    <img className="app-loading__logo" src="/images/rtg-logo-red.png" alt="" />
                    <div className="spinner" />
                    <span className="sr-only">Loading…</span>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

// Convenience hook for gating UI. Fail-open.
export const useFeature = (key) => {
    const ctx = useContext(AuthContext);
    return featureEnabled(ctx?.features, key);
};

// Convenience hook for gating UI on a permission key. Admins always pass.
export const usePermission = (key) => {
    const ctx = useContext(AuthContext);
    if (ctx?.user?.is_admin) return true;
    return Array.isArray(ctx?.permissions) && ctx.permissions.includes(key);
};
